import { AssetSelectStatus } from "../../../Components/Table/TableCellPackageBundleStatus";
import { AssetSubType } from "../../../Core/Api/Types/asset-sub-type";
import { AudioMix } from "../../../Core/Api/Types/audio-mix";
import { AudioMixLanguageCouple } from "../../../Core/Api/Types/audio-mix-language-couple";
import { AudioMixVersion } from "../../../Core/Api/Types/audio-mix-version";
import { DbKdmRecipientInfos } from "../../../Core/Api/Types/db-kdm-recipient-infos";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { SubtitleClosedType, SubtitleOption } from "../../../Core/Api/Types/subtitle-option";
import { TranskodeBundleAsset } from "../../../Core/Api/Types/transkode-bundle-asset";
import { TranskodeBundleAssetProfile } from "../../../Core/Api/Types/transkode-bundle-asset-profile";
import { TranskodeBundleComponent } from "../../../Core/Api/Types/transkode-bundle-component";
import { TranskodeGenericOptionsProfile } from "../../../Core/Api/Types/transkode-generic-options-profile";
import { TranskodeSubtitleFormatEnum } from "../../../Core/Api/Types/transkode-subtitle-format-enum";
import { XmlBundleDeliveryIdentityProfile } from "../../../Core/Api/Types/xml-bundle-delivery-identity-profile";
import { XmlDeliveryIdentityProfile } from "../../../Core/Api/Types/xml-delivery-identity-profile";
import { XmlDeliveryIdentityProfilesInfo } from "../../../Core/Api/Types/xml-delivery-identity-profiles-info";
import { XmlTechAudioMapping } from "../../../Core/Api/Types/xml-tech-audio-mapping";
import { XmlTechAudioMappingCondition } from "../../../Core/Api/Types/xml-tech-audio-mapping-condition";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { TgopField } from "../../../Redux/Reducers/redux-slice-basket";
import { TranskodeBundleComponentType } from "../../../Core/Api/Types/transkode-bundle-component-type";
import { XmlBundleDeliveryIdentityProfilesInfo } from "../../../Core/Api/Types/xml-bundle-delivery-identity-profiles-info";
import { TranskodeBundleComponentProfile } from "../../../Core/Api/Types/transkode-bundle-component-profile";
import { TranskodeTimelineSegmentType } from "../../../Core/Api/Types/transkode-timeline-segment-type";
import { t } from "i18next";

// Get audio label
export const GetAudioLabel = (
  value: AudioMixLanguageCouple,
  showNonAvailableTag: boolean = true,
  showMixEnum: boolean = true
): string => {
  let label = "";

  if (value.isMusicAndEffects) label = "M&E";
  else if (value.isMuted) label = "Mute";
  else {
    if (value.isAudioDescription) label = "AD " + value.language?.label + " (" + value.language?.value + ")";
    else if (value.isCommentary) label = "Commentary " + value.language?.label + " (" + value.language?.value + ")";
    else {
      label = value.language?.label + " (" + value.language?.value + ")";
      if (value.isOriginalVersion) label = "OV " + label;
      else label = "Dub " + label;
    }
  }

  return (
    label +
    (!value.mixEnum || !showMixEnum ? "" : " " + GetAudioMixEnumReadeable(value.mixEnum!)) +
    (showNonAvailableTag && !value.isAvailable ? " (N/A)" : "")
  );
};

// Get audio mapping label for bundlemaker modal
export const GetAudioMappingLabelBundleMaker = (
  mapping: XmlTechAudioMapping,
  values: AudioMixLanguageCouple[],
  language: PlatformLanguageCode | undefined
): string => {
  const conditionsText: string[] = [];

  if (!mapping.conditions) return "MUTED";
  let isMappingAvailable = true;
  mapping.conditions?.forEach((condition) => {
    const targetLanguage =
      condition.language?.value === AudioMixVersion.DynamicReplacement ? language : condition.language;

    const conditionTextElements: string[] = [];
    // Music and effects
    if (condition.isMusicAndEffects) conditionTextElements.push("M&E");
    // Audio mix
    conditionTextElements.push(GetAudioMixEnumReadeable(condition.mixEnum!));
    // Audio description
    if (condition.isAudioDescription) conditionTextElements.push("AD");
    // Language
    if (targetLanguage?.value) conditionTextElements.push(targetLanguage.value);

    conditionsText.push(conditionTextElements.join(" "));

    const isConditionAvailable = IsConditionAvailable(condition, values, targetLanguage || undefined);
    if (!isConditionAvailable) isMappingAvailable = false;
  });

  return conditionsText.join(" + ") + (isMappingAvailable ? "" : " - N/A");
};

const IsConditionAvailable = (
  condition: XmlTechAudioMappingCondition,
  values: AudioMixLanguageCouple[],
  language: PlatformLanguageCode | undefined
): boolean => {
  return (
    values.find(
      (x) =>
        x.isAvailable &&
        x.mixEnum === condition.mixEnum &&
        x.isAudioDescription === condition.isAudioDescription &&
        x.isMusicAndEffects === condition.isMusicAndEffects &&
        // If music and effects, language is not mandatory
        (condition.isMusicAndEffects ||
          (x.language?.value === language?.value &&
            (condition.language?.value === AudioMixVersion.DynamicReplacement ||
              condition.language?.value === language?.value)))
    ) !== undefined
  );
};

// Is audio is available for given mapping?
export const IsAudioAvailableByMapping = (
  mapping: XmlTechAudioMapping,
  values: AudioMixLanguageCouple[],
  language: PlatformLanguageCode | undefined
): boolean => {
  if (!values || values === null || !mapping || !mapping.conditions) return false;

  // Loop on each condition and check if condition is covered by available audio values
  for (let i = 0; i < mapping.conditions!.length; ++i) {
    const targetLanguage =
      mapping.conditions![i].language?.value === AudioMixVersion.DynamicReplacement
        ? language
        : mapping.conditions![i].language;

    if (!IsConditionAvailable(mapping.conditions![i], values, targetLanguage ?? undefined)) return false;
  }

  return true;
};

const IsAudioLanguageInMixes = (
  values: AudioMixLanguageCouple[],
  language: PlatformLanguageCode | undefined
): boolean => {
  return values.some((x) => x.language && x.language.value === language?.value);
};

// Get KDM recipient label
export const GetKdmRecipientLabel = (recipient: DbKdmRecipientInfos) => {
  return recipient.id === -1
    ? "Select all"
    : recipient.name +
        " " +
        recipient.city +
        " (Room " +
        recipient.roomName +
        ")" +
        (recipient.globecast !== null ? " (Globecast #" + recipient.globecast?.siteId + ")" : "");
};

// Get audio mix by mapping condition
export const GetAudioMixByMappingCondition = (
  condition: XmlTechAudioMappingCondition,
  item: DbOrderBasketItemInfos
): AudioMixLanguageCouple | undefined => {
  let titleOriginalLang = GetMetadataByTagName(item.titleInfos, "titleOriginalLanguage")?.value?.value;

  return FindAudioMixByMappingCondition(
    condition,
    item.selectedProduct?.settings?.tgop.audioMixes?.values,
    titleOriginalLang
  );
};

// Find audio mix by mapping condition
export function FindAudioMixByMappingCondition(
  condition: XmlTechAudioMappingCondition,
  audioMixesValues: AudioMixLanguageCouple[],
  titleOriginalLang: string,
  overrideLanguage?: string,
  lookForIsAvailable?: boolean // use to allow non available mapping to be returned.
): AudioMixLanguageCouple | undefined {
  if (!audioMixesValues) return undefined;
  // If dynamic replacement => user overriden language
  const _condition: XmlTechAudioMappingCondition = JSON.parse(JSON.stringify(condition));
  if (_condition.language && _condition.language?.value === AudioMixVersion.DynamicReplacement && overrideLanguage)
    _condition.language.value = overrideLanguage;

  return audioMixesValues.find(
    (x) =>
      (lookForIsAvailable === undefined ? x.isAvailable : x.isAvailable === lookForIsAvailable) &&
      x.mixEnum === _condition.mixEnum &&
      x.isAudioDescription === _condition.isAudioDescription &&
      x.isMusicAndEffects === _condition.isMusicAndEffects &&
      (!_condition.language ||
        _condition.isMusicAndEffects ||
        x.language?.value === _condition.language.value ||
        (_condition.language.value === "VO" && x.language?.value === titleOriginalLang))
  );
}

// Is audio mapping is IV
export const IsAudioMappingIV = (mappings: XmlTechAudioMapping[]) => {
  if (!mappings.length) return false; // In case of xml 'hideMappingIsVo="true"' => no mapping but not necessarily MusicAndEffects

  for (let i = 0; i < mappings.length; ++i) {
    for (let c = 0; c < mappings[i]!.conditions!.length; ++c) {
      if (!mappings[i].conditions![c].isMusicAndEffects) return false;
    }
  }

  return true;
};

// Make audio values
export const MakeAudioValues = (
  xmlProfile: XmlDeliveryIdentityProfile,
  item: DbOrderBasketItemInfos
): XmlTechAudioMapping[] => {
  if (!xmlProfile) return [];

  let titleOriginalLang: string[] = GetMetadataByTagName(item.titleInfos, "titleOriginalLanguage")?.value?.value;

  let array = JSON.parse(JSON.stringify(xmlProfile.audioInfos?.mappings!));
  let i = 0;

  array.forEach((mapping) => {
    let conditionsText: string[] = [];
    let hasAudioDescription: boolean = false;
    let languages: string[] = [];

    mapping.conditions?.forEach((condition) => {
      if (condition.isAudioDescription) hasAudioDescription = true;

      var trackIsOV =
        condition.language !== null && (condition.language.value === "VO" || condition.language.value === "OV");

      var trackLanguage = trackIsOV
        ? titleOriginalLang
          ? titleOriginalLang
          : "OV"
        : condition.language !== null
        ? condition.language.value
        : null;

      if (trackLanguage !== null && languages.indexOf(trackLanguage) === -1) languages.push(trackLanguage);

      let mapConditionText =
        (condition.isMusicAndEffects ? "M&E" : trackLanguage) +
        " " +
        GetAudioMixEnumReadeable(condition.mixEnum!) +
        (condition.isAudioDescription ? " AD" : "");

      conditionsText.push(mapConditionText);
    });

    // Set useful UI props to be able to filter mappings
    mapping.label = conditionsText.join(" + ");
    mapping.isAvailable = mapping.conditions?.every((x) => GetAudioMixByMappingCondition(x, item) !== undefined)!;
    if (!mapping.isAvailable) mapping.label = mapping.label + " - N/A";
    mapping.iteration = i;
    mapping.languages = languages;
    mapping.hasAudioDescription = hasAudioDescription;

    ++i;
  });

  return array.filter((x) => x.hideMappingIsVo === null || x.hideMappingIsVo !== titleOriginalLang);
};

// Make subtitle burned values
export const MakeSubtitleBurnedValues = (
  xmlProfile: XmlDeliveryIdentityProfile,
  tgop: TranskodeGenericOptionsProfile
): SubtitleOption[] => {
  if (!xmlProfile) return [];

  let array: SubtitleOption[] = [];

  xmlProfile.subtitleInfos?.burnedSubtitles?.forEach((sub) => {
    let match = tgop.subtitleBurned?.values?.find(
      (x) => x.type === sub.type && !x.isSDH && x.language?.value === sub.language?.value
    );

    if (match === undefined)
      array.push(
        new SubtitleOption({
          id: -1,
          language: sub.language,
          type: sub.type,
          isSDH: false,
          isAvailable: false,
          concernedAssetIds: [],
          toBeApplied: sub.toBeApplied,
        })
      );
    else array.push({ ...match, toBeApplied: sub.toBeApplied });
  });
  return array;
};

// Make subtitle sidecar values
export const MakeSubtitleSidecarValues = (
  xmlProfile: XmlDeliveryIdentityProfile,
  tgop: TranskodeGenericOptionsProfile
): SubtitleOption[] => {
  if (!xmlProfile) return [];

  let array: SubtitleOption[] = [];

  xmlProfile.subtitleInfos?.sidecarSubtitles
    ?.filter((x) => !x.isSDH)
    .forEach((sub) => {
      let closed = sub.format === TranskodeSubtitleFormatEnum.STL_CLOSED;

      let match = tgop.subtitleSidecarFiles?.values?.find(
        (x) =>
          x.type === sub.type &&
          !x.isSDH &&
          x.language?.value === sub.language?.value &&
          x.isClosed === closed &&
          x.sdhType === null
      );

      if (match === undefined)
        array.push(
          new SubtitleOption({
            id: -1,
            language: sub.language,
            type: sub.type,
            isSDH: false,
            isClosed: closed,
            isAvailable: false,
            format: sub.format,
            concernedAssetIds: [],
          })
        );
      else {
        // Check if available if there is no warn for this format
        let available = match.warns?.filter((x) => x.outputFormat === sub.format).length === 0;
        array.push({ ...match, format: sub.format, isAvailable: available });
      }
    });
  return array;
};

// Make subtitle sdh sidecar values
export const MakeSubtitleSdhSidecarValues = (
  xmlProfile: XmlDeliveryIdentityProfile,
  tgop: TranskodeGenericOptionsProfile
): SubtitleOption[] => {
  if (!xmlProfile) return [];

  let array: SubtitleOption[] = [];

  xmlProfile.subtitleInfos?.sidecarSubtitles
    ?.filter((x) => x.isSDH)
    .forEach((sub) => {
      let closed = sub.format === TranskodeSubtitleFormatEnum.STL_CLOSED || sub.closedType !== null;

      let match = tgop.subtitleSidecarFiles?.values?.find(
        (x) =>
          x.type === sub.type &&
          x.isSDH &&
          x.language?.value === sub.language?.value &&
          x.isClosed === closed &&
          (sub.sdhType === null || x.sdhType === sub.sdhType) &&
          (sub.closedType === null || x.closedType === sub.closedType)
      );

      if (match === undefined)
        array.push(
          new SubtitleOption({
            id: -1,
            language: sub.language,
            type: sub.type,
            isSDH: true,
            isClosed: closed,
            sdhType: sub.sdhType,
            closedType: sub.closedType,
            isAvailable: false,
            format: sub.format,
            concernedAssetIds: [],
          })
        );
      else {
        // Check if available if there is no warn for this format
        let available = match.warns?.filter((x) => x.outputFormat === sub.format).length === 0;
        array.push({ ...match, format: sub.format, isAvailable: available });
      }
    });
  return array;
};

// Get audio mix redeable
export const GetAudioMixEnumReadeable = (mix: AudioMix): string => {
  switch (mix) {
    case AudioMix.Mix_1_0:
      return "1.0";
    case AudioMix.Mix_1_0_1_0:
      return "1.0 + 1.0";
    case AudioMix.Mix_2_0:
      return "2.0";
    case AudioMix.Mix_5_1:
      return "5.1";
    case AudioMix.Mix_7_1:
      return "7.1";
    case AudioMix.Undefined:
      return "Undefined";
  }
};

// Get subtitle label
export const GetSubtitleLabel = (value: SubtitleOption, showNonAvailableTag: boolean = true): string => {
  if (!value) return "Unknown";

  let label =
    value.language?.label +
    " (" +
    value.language?.value +
    ") " +
    value.type +
    (value.sdhType ? ` ${value.sdhType}` : "") +
    " " +
    (value.isClosed ? `Closed${value.closedType ? ` ${value.closedType}` : ``}` : "Open") +
    (value.format ? ` ${value.format}` : "");

  return label + (showNonAvailableTag && !value.isAvailable ? " (N/A)" : "");
};

export interface MissingAssetReport {
  field: TgopField;
  message: string;
  locked: boolean; // If locked, config not possible, else just missing assets (but doable)
}

export interface BasketItemMissingAssetState {
  missing: boolean;
  fields: MissingAssetReport[];
}

// Get missing asset fields of a Tgop
export const GetTgopMissingAssetFields = (
  tgop: TranskodeGenericOptionsProfile,
  noDubbingCard: boolean,
  noTextless: boolean,
  noRestorationCard: boolean
): MissingAssetReport[] => {
  let missings: MissingAssetReport[] = [];

  if (!tgop || tgop === null || tgop === undefined) return missings;

  if (
    tgop.audioMixes !== undefined &&
    tgop.audioMixes !== null &&
    tgop.audioMixes.selectedValues !== null &&
    tgop.audioMixes.selectedValues !== undefined &&
    tgop.audioMixes.selectedValues.filter((x) => !x.isAvailable).length > 0
  )
    missings.push({
      field: TgopField.AudioMixes,
      message: t("field:errorMessage.audioMixesSelectionUnavailable"),
      locked: false,
    });
  if (
    (noDubbingCard === undefined || (noDubbingCard !== undefined && !noDubbingCard)) &&
    tgop.dubCards &&
    tgop.dubCards !== null &&
    tgop.dubCards.selectedValues !== null &&
    tgop.dubCards.selectedValues !== undefined &&
    tgop.dubCards.selectedValues.filter((x) => !x.isAvailable).length > 0
  )
    missings.push({
      field: TgopField.DubbingCard,
      message: t("field:errorMessage.dubbingCardUnavailable"),
      locked: false,
    });
  if (
    tgop.fileFormat !== undefined &&
    tgop.fileFormat !== null &&
    tgop.fileFormat.selectedValue !== null &&
    tgop.fileFormat.selectedValue !== undefined &&
    !tgop.fileFormat.selectedValue.isAvailable
  )
    missings.push({
      field: TgopField.FileFormat,
      message: t("field:errorMessage.outputFileFormatNotDoable", { fileFormat: tgop.fileFormat.selectedValue.label }),
      locked: false,
    });
  if (
    tgop.imageSidecarFiles !== undefined &&
    tgop.imageSidecarFiles !== null &&
    tgop.imageSidecarFiles.selectedValues !== null &&
    tgop.imageSidecarFiles.selectedValues !== undefined &&
    tgop.imageSidecarFiles.selectedValues.filter((x) => !x.isAvailable).length > 0
  )
    missings.push({
      field: TgopField.ImageSidecarFiles,
      message: t("field:errorMessage.imageSidecarFilesUnavailable"),
      locked: false,
    });
  if (
    tgop.picture !== undefined &&
    tgop.picture !== null &&
    tgop.picture.selectedValue !== null &&
    tgop.picture.selectedValue !== undefined &&
    !tgop.picture.selectedValue.isAvailable
  )
    missings.push({
      field: TgopField.Picture,
      message: t("field:errorMessage.pictureSelectionUnavailable"),
      locked: false,
    });
  if (
    tgop.subtitleBurned !== undefined &&
    tgop.subtitleBurned !== null &&
    tgop.subtitleBurned.selectedValue !== null &&
    tgop.subtitleBurned.selectedValue !== undefined &&
    !tgop.subtitleBurned.selectedValue.isAvailable
  )
    missings.push({
      field: TgopField.SubtitleBurned,
      message: t("field:errorMessage.burnedSubtitleSelectionUnavailable"),
      locked: false,
    });
  if (
    tgop.subtitleSidecarFiles !== undefined &&
    tgop.subtitleSidecarFiles !== null &&
    tgop.subtitleSidecarFiles.selectedValues !== null &&
    tgop.subtitleSidecarFiles.selectedValues !== undefined &&
    tgop.subtitleSidecarFiles.selectedValues.filter((x) => x !== null && x !== undefined && !x.isAvailable).length > 0
  )
    missings.push({
      field: TgopField.SubtitleSidecarFiles,
      message: t("field:errorMessage.subtitleSidecarVersionsUnavailable"),
      locked: false,
    });

  if (
    tgop.subtitleSidecarFiles !== undefined &&
    tgop.subtitleSidecarFiles !== null &&
    tgop.subtitleSidecarFiles.selectedValues !== null &&
    tgop.subtitleSidecarFiles.selectedValues !== undefined &&
    tgop.subtitleSidecarFiles.selectedValues.length > 0
  ) {
    let selectedFormat = tgop.fileFormatSubtitleSidecar?.selectedValue?.format ?? undefined;

    for (let i = 0; i < tgop.subtitleSidecarFiles.selectedValues.length; ++i) {
      let taggedFormat = tgop.subtitleSidecarFiles.selectedValues[i].format;

      if (
        tgop.subtitleSidecarFiles.selectedValues[i].warns !== null &&
        tgop.subtitleSidecarFiles.selectedValues[i].warns!.filter(
          (w) => w.outputFormat === selectedFormat || w.outputFormat === taggedFormat
        ).length > 0
      )
        missings.push({
          field: TgopField.SubtitleSidecarFiles,
          message: t("field:errorMessage.subtitleIncompatibleWithOutputFormat", {
            subtitleLabel: GetSubtitleLabel(tgop.subtitleSidecarFiles.selectedValues[i], false),
            outputFormat: selectedFormat ?? taggedFormat ?? "Unknown",
          }),
          locked: true,
        });
    }
  }

  if (
    (noTextless === undefined || (noTextless !== undefined && !noTextless)) &&
    tgop.textlessMaterials !== undefined &&
    tgop.textlessMaterials !== null &&
    tgop.textlessMaterials.selectedValue !== null &&
    tgop.textlessMaterials.selectedValue !== undefined &&
    !tgop.textlessMaterials.selectedValue.isAvailable
  )
    missings.push({
      field: TgopField.TextlessMaterial,
      message: t("field:errorMessage.textlessMaterialUnavailable"),
      locked: false,
    });
  if (
    (noRestorationCard === undefined || (noRestorationCard !== undefined && !noRestorationCard)) &&
    tgop.restorationCards !== undefined &&
    tgop.restorationCards !== null &&
    tgop.restorationCards.selectedValue !== null &&
    tgop.restorationCards.selectedValue !== undefined &&
    !tgop.restorationCards.selectedValue.isAvailable
  )
    missings.push({
      field: TgopField.RestorationCard,
      message: t("field:errorMessage.restorationCardUnavailable"),
      locked: false,
    });

  return missings;
};

// Get missing asset fields of a bundle maker component
const GetBundleMakerMissingAssetFields = (
  component: TranskodeBundleComponent,
  asset: TranskodeBundleAsset,
  titleOriginalLang: PlatformLanguageCode,
  index?: number,
  xmlProfile?: XmlBundleDeliveryIdentityProfilesInfo
): MissingAssetReport | null => {
  let status: AssetSelectStatus = BundleMakerGetAssetSelectStatus(
    asset,
    component,
    titleOriginalLang,
    xmlProfile?.profile ?? undefined
  );
  if (status === AssetSelectStatus.NotSelected)
    return {
      field: TgopField.Fps,
      message: t("field:errorMessage.componentNotSelected", {
        componentType: component.type,
        assetType: asset.type,
      }),
      locked: false,
    };
  if (status === AssetSelectStatus.MissingAsset)
    return {
      field: TgopField.Fps,
      message: t("field:errorMessage.componentHasMissingAsset", {
        componentType: component.type,
        assetType: asset.type,
        index: (index ?? 0) + 1,
      }),
      locked: false,
    };

  return null;
};

// Match asset to profile asset?
export const MatchAudioAssetToProfile = (
  asset: TranskodeBundleAsset,
  assetProfile: TranskodeBundleAssetProfile
): boolean => {
  if (asset.type !== ObjectType.Audio && asset.type !== ObjectType.Video) return true;
  return asset.assetIndexSource === assetProfile.assetIndexSource;
};

// Get asset selection status in bundle maker
export const BundleMakerGetAssetSelectStatus = (
  asset: TranskodeBundleAsset,
  component: TranskodeBundleComponent | undefined,
  titleOriginalLang: PlatformLanguageCode,
  xmlProfile?: XmlBundleDeliveryIdentityProfile
): AssetSelectStatus => {
  if (!asset) return AssetSelectStatus.Ok;
  if (!asset.transcodeGenericOptionsProfile) return AssetSelectStatus.NotSelected;

  let type = asset.type;

  if (
    type === ObjectType.Image &&
    asset.transcodeGenericOptionsProfile.picture?.selectedValue?.contentSubType === "Dubbing Card"
  )
    type = ObjectType.Video;
  // Declare funciton to compute missing asset on audio, it is used for audio asset AND video asset cause we can select audio mapping
  const computeAudioMissingAsset = () => {
    let isIV = asset.subType === AssetSubType.AudioMusicAndEffects;
    // Override language is mandatory for audio, not video
    if (
      asset.audioMixMappingId === null ||
      asset.audioMixMappingId < 0 ||
      (asset.country && asset.type !== ObjectType.Video && !asset.overrideLanguage && !isIV) ||
      !component
    )
      return AssetSelectStatus.NotSelected;

    let lang: PlatformLanguageCode | undefined;
    let profileComponent: TranskodeBundleComponentProfile | undefined = xmlProfile?.components.find(
      (x) => x.type === component.type
    );
    let audioMappings: XmlTechAudioMapping[] = profileComponent?.assets.find((x) => MatchAudioAssetToProfile(asset, x))
      ?.audioInfos?.mappings!;
    if (!audioMappings) return AssetSelectStatus.Ok;

    let audioMappingId = asset.audioMixMappingId;
    // This is the case where we add video audio language (if !== from country.language && titleOriginalLang) => get video asset audio language
    if (asset.type === ObjectType.Video) {
      let assetRef = asset.transcodeGenericOptionsProfile?.audioMixes?.values?.find(
        (x) =>
          JSON.stringify([...(x.concernedAssetIds ?? [])].sort()) ===
          JSON.stringify(
            [...(asset.transcodeGenericOptionsProfile?.picture?.selectedValue?.concernedAssetIds ?? [])].sort()
          )
      );
      if (assetRef?.language) lang = assetRef.language;
    }

    if (lang === undefined) {
      lang = !isIV
        ? asset.country && asset.overrideLanguage
          ? {
              id: 0,
              externalId: 0,
              value: asset.overrideLanguage,
              label: "",
              rfc5646: "",
            }
          : titleOriginalLang
        : undefined;
    }

    let audioValues: AudioMixLanguageCouple[] = asset.transcodeGenericOptionsProfile!.audioMixes?.values!;
    if (audioValues == null || audioValues.length < 1)
      audioValues = asset.transcodeGenericOptionsProfile!.audioMixes?.selectedValues!;

    if (
      audioMappings &&
      audioMappings.length > 0 &&
      !(audioMappings.length === 1 && audioMappings[0].conditions === null) &&
      !IsAudioAvailableByMapping(audioMappings[audioMappingId], audioValues, lang)
    )
      return AssetSelectStatus.MissingAsset;

    return AssetSelectStatus.Ok;
  };

  const isDubbingCardMandatory = () => {
    if (component?.overrideDoNotUseDubCard) return false;

    // special case
    // like if not dubed
    // or too old be in the rule of dubing card.
    let profileComponent: TranskodeBundleComponentProfile | undefined = xmlProfile?.components.find(
      (x) => x.type === component?.type
    );

    let audioMappings: XmlTechAudioMapping[] = profileComponent?.assets.find((x) => MatchAudioAssetToProfile(asset, x))
      ?.audioInfos?.mappings!;
    if (!audioMappings) return false;

    let timeline = profileComponent?.assets[asset.assetIndexSource ?? 0].timeline?.segments?.filter(
      (x) => x.type === TranskodeTimelineSegmentType.DubCard
    );

    if (timeline?.length ?? 0 < 1) return false;

    let result = timeline?.some((x) => {
      x.conditionByAudioLanguageIs != null &&
        x.conditionByAudioLanguageIs.value !== titleOriginalLang?.value &&
        IsAudioLanguageInMixes(
          asset.transcodeGenericOptionsProfile!.audioMixes?.selectedValues!,
          x.conditionByAudioLanguageIs
        );
    });

    return result;
  };

  switch (type) {
    case ObjectType.Video:
      if (
        asset.transcodeGenericOptionsProfile!.dubCards !== null &&
        isDubbingCardMandatory() &&
        (asset.transcodeGenericOptionsProfile!.dubCards?.selectedValues == null ||
          asset.transcodeGenericOptionsProfile!.dubCards?.selectedValues.length === 0 ||
          asset.transcodeGenericOptionsProfile!.dubCards?.selectedValues?.some((x) => !x.isAvailable))
      )
        return AssetSelectStatus.MissingAsset;

      if (
        !asset.transcodeGenericOptionsProfile!.picture?.selectedValue &&
        (!asset.transcodeGenericOptionsProfile!.picture?.values ||
          asset.transcodeGenericOptionsProfile!.picture?.values.length === 0)
      )
        return AssetSelectStatus.MissingAsset;
      if (!asset.transcodeGenericOptionsProfile!.picture?.selectedValue) return AssetSelectStatus.NotSelected;
      else if (!asset.transcodeGenericOptionsProfile!.picture?.selectedValue.isAvailable)
        return AssetSelectStatus.MissingAsset;

      if (component?.type === TranskodeBundleComponentType.DubCard) return AssetSelectStatus.Ok;
      else return computeAudioMissingAsset();

    case ObjectType.Audio:
      return computeAudioMissingAsset();

      break;
    case ObjectType.Subtitle:
      if (
        (!asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.selectedValues ||
          asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.selectedValues.length === 0) &&
        (!asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.values ||
          asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.values.length === 0)
      )
        return AssetSelectStatus.MissingAsset;
      if (
        !asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.selectedValues ||
        asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.selectedValues.length === 0
      )
        return AssetSelectStatus.NotSelected;
      else if (
        asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles?.selectedValues.filter((x) => !x.isAvailable)
          .length > 0
      )
        return AssetSelectStatus.MissingAsset;

      break;
    case ObjectType.Image:
      if (
        (!asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.selectedValues ||
          asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.selectedValues.length === 0) &&
        (!asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.values ||
          asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.values.length === 0)
      )
        return AssetSelectStatus.MissingAsset;
      if (
        !asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.selectedValues ||
        asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.selectedValues.length === 0
      )
        return AssetSelectStatus.NotSelected;
      else if (
        asset.transcodeGenericOptionsProfile!.imageSidecarFiles?.selectedValues.filter((x) => !x.isAvailable).length > 0
      )
        return AssetSelectStatus.MissingAsset;

      break;
  }

  return AssetSelectStatus.Ok;
};

///
/// inteligence to select the good profile to a bundle.
///
export const SelectBundleProfile = (
  profiles?: XmlBundleDeliveryIdentityProfilesInfo[],
  selectedBroadcasterId?: number,
  titleOriginalLang?: string
): XmlBundleDeliveryIdentityProfilesInfo | undefined => {
  if (!profiles || !selectedBroadcasterId || !titleOriginalLang) return undefined;

  console.log("product::bundlebyprofile:: broadcasterId = " + selectedBroadcasterId);
  if (profiles) {
    let profile = profiles?.find(
      (x) => x.broadcasterId === selectedBroadcasterId && x.languagesGeneratedWith?.includes(titleOriginalLang)
    );

    return profile;
  }
};

// Get Basket item missing asset state
export const IsBasketItemMissingAsset = (
  item: DbOrderBasketItemInfos,
  xmlProfiles?: XmlDeliveryIdentityProfilesInfo[] | null, // PAD Profile
  xmlBundleProfiles?: XmlBundleDeliveryIdentityProfilesInfo[] | null // bundleMaker Profile
): BasketItemMissingAssetState => {
  let response: BasketItemMissingAssetState = {
    missing: false,
    fields: [],
  };

  if (
    !item?.selectedProduct ||
    !item?.selectedProduct?.settings ||
    (item?.selectedProduct?.type !== OrderProductType.TranscodeByProfile &&
      item?.selectedProduct?.type !== OrderProductType.TranscodeBundleByProfile &&
      item?.selectedProduct?.type !== OrderProductType.TranscodeGeneric &&
      item?.selectedProduct?.type !== OrderProductType.TranscodeDcp &&
      item?.selectedProduct?.type !== OrderProductType.TranscodeGenericSingleFile)
  )
    return response;

  switch (item?.selectedProduct?.type) {
    case OrderProductType.TranscodeGenericSingleFile:
      response.fields = GetTgopMissingAssetFields(item?.selectedProduct?.settings, true, true, true);
      break;
    case OrderProductType.TranscodeGeneric:
    case OrderProductType.TranscodeByProfile:
      response.fields = GetTgopMissingAssetFields(
        item?.selectedProduct?.settings.tgop,
        item.selectedProduct.settings.overrideDoNotUseDubCards,
        item.selectedProduct.settings.overrideDoNotUseTextless,
        item.selectedProduct.settings.overrideDoNotUseRestorationCard
      );
      break;
    case OrderProductType.TranscodeBundleByProfile:
      // TODO loop on components->assets->tgop
      item.selectedProduct.settings.components.forEach((component: TranskodeBundleComponent) => {
        component.assets?.forEach((asset, index) => {
          if (component.type === null || !xmlBundleProfiles) return;
          let fields = GetBundleMakerMissingAssetFields(
            component,
            asset,
            GetMetadataByTagName(item.titleInfos, "titleOriginalLanguage")?.value,
            index,
            SelectBundleProfile(
              xmlBundleProfiles,
              item.selectedBroadcaster?.id,
              GetMetadataByTagName(item?.titleInfos, "titleOriginalLanguage")?.value.value
            )
          );
          if (fields !== null) response.fields.push(fields);
        });
      });
      break;
    case OrderProductType.TranscodeDcp:
      response.fields = GetTgopMissingAssetFields(item?.selectedProduct?.settings.cpls.selectedValue, true, true, true);
      if (
        item?.selectedProduct?.settings.cpls !== undefined &&
        item?.selectedProduct?.settings.cpls !== null &&
        item?.selectedProduct?.settings.cpls.selectedValue !== null &&
        !item?.selectedProduct?.settings.cpls.selectedValue.isAvailable
      )
        response.fields.push({
          field: TgopField.DcpCpl,
          message: !item?.selectedProduct?.settings.cpls.selectedValue.availableVideo
            ? t("field:errorMessage.videoUnavailableOnDCPPackage")
            : t("field:errorMessage.dkdmMissingDecryptionImpossible"),
          locked: true,
        });
      break;
  }

  response.missing = response.fields.length > 0;

  return response;
};

// Copy product details to all others of same type
export const CopyToAllBasketItems = (
  allItems: DbOrderBasketItemInfos[],
  sourceItemId: number,
  onlyMainProps: boolean,
  profiles?: XmlDeliveryIdentityProfilesInfo[] | null
): DbOrderBasketItemInfos[] => {
  let item = allItems.find((x) => x.id === sourceItemId);

  if (!item || !item?.selectedProduct) return [];

  // Make a deep copy of destination objects to avoid read-only behavior
  let srcItems = allItems.filter(
    (x) =>
      //   x.titleId === item?.titleId &&
      (x.selectedProduct?.type === item?.selectedProduct?.type || x.selectedProduct === null) &&
      x.id !== sourceItemId &&
      x.objectLayer === item?.objectLayer
  );

  let items = JSON.parse(JSON.stringify(srcItems));
  let content = JSON.parse(JSON.stringify(item.selectedContent));
  let broadcaster = JSON.parse(JSON.stringify(item.selectedBroadcaster));

  console.debug(
    "basket:CopyToAllBasketItems() copy config will be applied on " +
      items.length +
      " objects in basket (onlyMainProps = " +
      onlyMainProps +
      ")"
  );

  for (let i = 0; i < items.length; ++i) {
    // Copy props in dest
    if (onlyMainProps) {
      items[i].selectedProduct = items[i].availableProducts.find((x) => x.id === item!.selectedProduct!.id);

      items[i].selectedBroadcaster = broadcaster;

      if (items[i].titleId === item.titleId) items[i].selectedContent = content;
      else {
        // Match content by contentType metadata
        if (item!.selectedContent !== null && items[i].selectedContent === null) {
          let srcContentType = item!.selectedContent.name;
          let contentMatch = items[i].assetInfos.childObjects.find((x) => x.name === srcContentType);

          if (contentMatch) items[i].selectedContent = contentMatch;
          else {
            if (items[i].selectedContent === null && items[i].objectLayer === ObjectLayer.Title) continue;
          }
        }
      }
    }

    if (onlyMainProps) continue;

    //Scheduling
    items[i].scheduleTime = item?.scheduleTime;

    switch (item?.selectedProduct?.type) {
      case OrderProductType.AntipiracyYoutube:
        items[i].selectedProduct!.settings.movieLabels = item?.selectedProduct?.settings.movieLabels;
        items[i].selectedProduct!.settings.policyName = item?.selectedProduct?.settings.policyName;
        items[i].selectedProduct!.settings.territoriesCodes = item?.selectedProduct?.settings.territoriesCodes;
        items[i].selectedProduct!.settings.deliveryMode = item?.selectedProduct?.settings.deliveryMode;
        break;

      case OrderProductType.YoutubeChannelDelivery:
        items[i].selectedProduct!.settings.channel = item?.selectedProduct?.settings.channel;
        items[i].selectedProduct!.settings.movieLabels = item?.selectedProduct?.settings.movieLabels;
        items[i].selectedProduct!.settings.title = item?.selectedProduct?.settings.title;
        items[i].selectedProduct!.settings.description = item?.selectedProduct?.settings.description;
        items[i].selectedProduct!.settings.keywords = item?.selectedProduct?.settings.keywords;
        items[i].selectedProduct!.settings.category = item?.selectedProduct?.settings.category;
        items[i].selectedProduct!.settings.privacy = item?.selectedProduct?.settings.privacy;
        items[i].selectedProduct!.settings.territoriesCodes = item?.selectedProduct?.settings.territoriesCodes;
        items[i].selectedProduct!.settings.blockOutsideOwnership =
          item?.selectedProduct?.settings.blockOutsideOwnership;
        items[i].selectedProduct!.settings.usagePolicy = item?.selectedProduct?.settings.usagePolicy;
        items[i].selectedProduct!.settings.enableContentId = item?.selectedProduct?.settings.enableContentId;
        items[i].selectedProduct!.settings.playlistId = item?.selectedProduct?.settings.playlistId;
        items[i].selectedProduct!.settings.isMadeForKids = item?.selectedProduct?.settings.isMadeForKids;
        items[i].selectedProduct!.settings.preRoll = item?.selectedProduct?.settings.preRoll;
        items[i].selectedProduct!.settings.postRoll = item?.selectedProduct?.settings.postRoll;
        break;

      case OrderProductType.DcpReferenceFileStitched:
      case OrderProductType.DcpReferenceFileSimple:
        // Security overlay option
        items[i].selectedProduct!.settings.overlayTextPlaceholder =
          item?.selectedProduct?.settings.overlayTextPlaceholder;
        items[i].selectedProduct!.watermarkTemplate = item?.selectedProduct?.watermarkTemplate;

        // Watermarking
        items[i].selectedProduct!.watermarkSecurity = item?.selectedProduct?.watermarkSecurity;

        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;
        break;

      case OrderProductType.FaspexSend:
        // Delivery Mode
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;
        items[i].selectedProduct!.proxy = item?.selectedProduct?.proxy;

        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        break;

      case OrderProductType.TranscodeSequence:
      case OrderProductType.TranscodeGenericSingleFile:
      case OrderProductType.TranscodeDownscaleAsMezz:
        items[i].selectedProduct!.settings.tgop.isAudioOnly = item?.selectedProduct?.settings.tgop.isAudioOnly;
        items[i].selectedProduct!.settings.isAudioOnly = item?.selectedProduct?.settings.isAudioOnly;

        // Delivery Mode
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;

        // file format
        if (
          items[i].selectedProduct!.settings.fileFormat.values.filter(
            (v) => v && v.id === item?.selectedProduct?.settings.fileFormat.selectedValue?.id
          ).length > 0
        )
          items[i].selectedProduct!.settings.fileFormat.selectedValue = items[
            i
          ].selectedProduct!.settings.fileFormat.values.find(
            (f) => f.id === item?.selectedProduct?.settings.fileFormat.selectedValue.id
          );

        // frame rate
        if (
          items[i].selectedProduct!.settings.frameRate.values.filter(
            (v) => JSON.stringify(v) === JSON.stringify(item?.selectedProduct?.settings.frameRate.selectedValue)
          ).length > 0
        )
          items[i].selectedProduct!.settings.frameRate.selectedValue =
            item?.selectedProduct?.settings.frameRate.selectedValue;

        // video field order
        if (
          !item?.selectedProduct?.settings.tgop.isAudioOnly &&
          items[i].selectedProduct!.settings.videoFieldOrder.values.filter(
            (v) => JSON.stringify(v) === JSON.stringify(item?.selectedProduct?.settings.videoFieldOrder.selectedValue)
          ).length > 0
        )
          items[i].selectedProduct!.settings.videoFieldOrder.selectedValue =
            item?.selectedProduct?.settings.videoFieldOrder.selectedValue;

        // Picture options
        items[i].selectedProduct!.settings.videoFilters = item?.selectedProduct?.settings.videoFilters;
        items[i].selectedProduct!.settings.burnTimecode = item?.selectedProduct?.settings.burnTimecode;

        // Security overlay
        items[i].selectedProduct!.settings.overlayTextPlaceholder =
          item?.selectedProduct?.settings.overlayTextPlaceholder;
        items[i].selectedProduct!.watermarkTemplate = item?.selectedProduct?.watermarkTemplate;

        // Watermarking
        items[i].selectedProduct!.watermarkSecurity = item?.selectedProduct?.watermarkSecurity;

        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;
        break;

      case OrderProductType.TranscodeByProfile:
      case OrderProductType.TranscodeByProfileAndPrintToTape:
        // to let save options possible
        if (items[i].selectedProduct!.settings.needExternalBroadcasterId)
          items[i].selectedProduct!.settings.externalBroadcasterId = GlobalConst.BROADCASTER_ID_NEEDED;

        // video profile (must be set first)
        items[i].selectedProduct!.settings.videoProfile = item?.selectedProduct?.settings.videoProfile;

        // no dubcards
        items[i].selectedProduct!.settings.overrideDoNotUseDubCards =
          item?.selectedProduct?.settings.overrideDoNotUseDubCards;

        // no textless
        items[i].selectedProduct!.settings.overrideDoNotUseTextless =
          item?.selectedProduct?.settings.overrideDoNotUseTextless;

        // picture format
        if (
          item?.selectedProduct?.settings.tgop.picture.selectedValue &&
          items[i].selectedProduct!.settings.tgop.picture.values.filter(
            (v) =>
              v.language?.value === item?.selectedProduct?.settings.tgop.picture.selectedValue.language?.value &&
              v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
              v.isAvailable
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.picture.selectedValue = items[
            i
          ].selectedProduct!.settings.tgop.picture.values.find(
            (f) =>
              f.language?.value === item?.selectedProduct?.settings.tgop.picture.selectedValue.language?.value &&
              f.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
              f.isAvailable
          );
        else if (
          item?.selectedProduct?.settings.tgop.picture.selectedValue &&
          items[i].selectedProduct!.settings.tgop.picture.values.filter(
            (v) =>
              v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
              v.isAvailable
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.picture.selectedValue = items[
            i
          ].selectedProduct!.settings.tgop.picture.values.find(
            (f) =>
              f.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
              f.isAvailable
          );

        // Getting profile we will need for the next configs
        let allProfiles = profiles?.find((x) => x.broadcasterId === item?.selectedBroadcaster?.id)?.profiles;
        let profile: XmlDeliveryIdentityProfile | undefined;

        if (item?.selectedProduct?.settings.videoProfile) {
          profile = allProfiles?.find(
            (x) => x.videoInfos?.videoProfile === item?.selectedProduct?.settings.videoProfile
          );
        } else if (allProfiles && allProfiles.length > 0) {
          profile = allProfiles[0];
        }

        if (profile) {
          // file format
          if (
            items[i].selectedProduct!.settings.tgop.fileFormat.selectedValue.videoProfile !==
            item?.selectedProduct?.settings.videoProfile
          )
            items[i].selectedProduct!.settings.tgop.fileFormat.selectedValue = items[
              i
            ].selectedProduct!.settings.tgop.fileFormat.values.find(
              (f) =>
                f.isAvailable &&
                f.videoProfile === profile?.videoInfos?.videoProfile &&
                f.wrapper === profile?.videoInfos?.wrapper &&
                f.chromaSampling === profile?.videoInfos?.chromaSampling &&
                f.codec === profile?.videoInfos?.codec &&
                f.contentType === items[i].selectedContent.name &&
                f.contentSubType === item?.selectedProduct?.settings.tgop.picture.selectedValue.contentSubType
            );

          // audio mix
          let audioVersions = MakeAudioValues(profile, items[i]);
          if (
            audioVersions.filter((m) => m.iteration === item?.selectedProduct?.settings.overrideAudioMixMappingId)
              .length > 0
          )
            items[i].selectedProduct!.settings.overrideAudioMixMappingId =
              item?.selectedProduct?.settings.overrideAudioMixMappingId;

          // burned subtitles
          let burnedSubtitleVersions = MakeSubtitleBurnedValues(profile, items[i].selectedProduct!.settings.tgop);
          if (
            item.selectedProduct.settings.tgop.subtitleBurned.selectedValue === null ||
            item.selectedProduct.settings.tgop.subtitleBurned.selectedValue === undefined
          )
            items[i].selectedProduct.settings.tgop.subtitleBurned.selectedValue = null;
          else if (
            burnedSubtitleVersions.filter(
              (v) =>
                v.language?.value ===
                  item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.language?.value &&
                v.type === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.type &&
                v.isSDH === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.isSDH &&
                v.isAvailable
            ).length > 0
          )
            items[i].selectedProduct.settings.tgop.subtitleBurned.selectedValue =
              item.selectedProduct.settings.tgop.subtitleBurned.selectedValue;

          // sidecar subtitles && SDH sidecar subtitles
          if (
            item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues === null ||
            item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues === undefined
          )
            items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues = null;
          else {
            // sidecar subtitles
            let selectedSidecarSubtitle = item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues.find(
              (v) => !v.isSDH
            );
            let SidecarSubtitleVersions = MakeSubtitleSidecarValues(profile, items[i].selectedProduct!.settings.tgop);
            if (selectedSidecarSubtitle) {
              let SidecarSubtitleVersion = SidecarSubtitleVersions.filter(
                (v) =>
                  v.type === selectedSidecarSubtitle.type &&
                  v.isSDH === selectedSidecarSubtitle.isSDH &&
                  v.isAvailable === selectedSidecarSubtitle.isAvailable &&
                  v.isClosed === selectedSidecarSubtitle.isClosed &&
                  v.language?.value === selectedSidecarSubtitle.language.value
              );
              if (SidecarSubtitleVersion.length > 0)
                items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues.push(
                  ...SidecarSubtitleVersion
                );
            }

            // SDH sidecar subtitles
            let selectedSdhSidecarSubtitle =
              item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues.find((v) => v.isSDH);

            let SdhSidecarSubtitleVersions = MakeSubtitleSdhSidecarValues(
              profile,
              items[i].selectedProduct!.settings.tgop
            );

            if (selectedSdhSidecarSubtitle) {
              let SidecarSdhSubtitleVersion = SdhSidecarSubtitleVersions.filter(
                (v) =>
                  v.type === selectedSdhSidecarSubtitle.type &&
                  v.isSDH === selectedSdhSidecarSubtitle.isSDH &&
                  v.isAvailable === selectedSdhSidecarSubtitle.isAvailable &&
                  v.isClosed === selectedSdhSidecarSubtitle.isClosed &&
                  v.language?.value === selectedSdhSidecarSubtitle.language.value
              );
              if (SidecarSdhSubtitleVersion.length > 0)
                items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues.push(
                  ...SidecarSdhSubtitleVersion
                );
            }
          }
        }

        // Watermarking
        items[i].selectedProduct!.watermarkSecurity = item?.selectedProduct?.watermarkSecurity;

        break;

      case OrderProductType.TranscodeComponentsLegacy:
        items[i].selectedProduct!.settings.tempAudioLanguagesCodes =
          item?.selectedProduct?.settings.tempAudioLanguagesCodes;
        items[i].selectedProduct!.settings.tempSubtitleLanguagesCodes =
          item?.selectedProduct?.settings.tempSubtitleLanguagesCodes;
        items[i].selectedProduct!.settings.tempTerritoriesCodes = item?.selectedProduct?.settings.tempTerritoriesCodes;
        break;

      case OrderProductType.TranscodeGeneric:
        items[i].selectedProduct!.settings.tgop.isAudioOnly = item?.selectedProduct?.settings.tgop.isAudioOnly;

        // Delivery Mode
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;

        // file format
        if (
          item?.selectedProduct?.settings.tgop.fileFormat.selectedValue !== null &&
          items[i].selectedProduct!.settings.tgop.fileFormat.values.filter(
            (v) => v.id === item?.selectedProduct?.settings.tgop.fileFormat.selectedValue.id
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.fileFormat.selectedValue = items[
            i
          ].selectedProduct!.settings.tgop.fileFormat.values.find(
            (f) => f.id === item?.selectedProduct?.settings.tgop.fileFormat.selectedValue.id
          );

        // frame rate
        if (
          items[i].selectedProduct!.settings.tgop.frameRate.values.filter(
            (v) => JSON.stringify(v) === JSON.stringify(item?.selectedProduct?.settings.tgop.frameRate.selectedValue)
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.frameRate.selectedValue =
            item?.selectedProduct?.settings.tgop.frameRate.selectedValue;

        // video field order
        if (
          items[i].selectedProduct!.settings.tgop.videoFieldOrder.values.filter(
            (v) =>
              JSON.stringify(v) === JSON.stringify(item?.selectedProduct?.settings.tgop.videoFieldOrder.selectedValue)
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.videoFieldOrder.selectedValue =
            item?.selectedProduct?.settings.tgop.videoFieldOrder.selectedValue;

        // audio mixes
        items[i].selectedProduct!.settings.tgop.audioMixes.selectedValues = [];
        item?.selectedProduct?.settings.tgop.audioMixes.selectedValues.forEach((value) => {
          if (
            items[i].selectedProduct!.settings.tgop.audioMixes.values.filter(
              (v) =>
                v.mixEnum === value.mixEnum &&
                JSON.stringify(v.language) === JSON.stringify(value.language) &&
                v.isAudioDescription === value.isAudioDescription &&
                v.isCommentary === value.isCommentary &&
                v.isMusicAndEffects === value.isMusicAndEffects &&
                v.isAvailable
            ).length > 0
          ) {
            items[i].selectedProduct!.settings.tgop.audioMixes.selectedValues.push(
              items[i].selectedProduct!.settings.tgop.audioMixes.values.find(
                (f) =>
                  f.mixEnum === value.mixEnum &&
                  JSON.stringify(f.language) === JSON.stringify(value.language) &&
                  f.isAudioDescription === value.isAudioDescription &&
                  f.isCommentary === value.isCommentary &&
                  f.isMusicAndEffects === value.isMusicAndEffects &&
                  f.isAvailable
              )
            );
          }
        });

        // picture
        if (
          !item?.selectedProduct?.settings.tgop.isAudioOnly &&
          item?.selectedProduct?.settings.tgop.picture.selectedValue !== null
        ) {
          if (
            items[i].selectedProduct!.settings.tgop.picture.values.filter(
              (v) =>
                v.language?.value === item?.selectedProduct?.settings.tgop.picture.selectedValue.language?.value &&
                v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
                v.isAvailable
            ).length > 0
          ) {
            console.debug(
              "copy: picture found match OK to copy! " + item?.selectedProduct?.settings.tgop.picture.selectedValue.id
            );
            items[i].selectedProduct!.settings.tgop.picture.selectedValue = items[
              i
            ].selectedProduct!.settings.tgop.picture.values.find(
              (v) =>
                v.language?.value === item?.selectedProduct?.settings.tgop.picture.selectedValue.language?.value &&
                v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
                v.isAvailable
            );
          } else if (
            items[i].selectedProduct!.settings.tgop.picture.values.filter(
              (v) =>
                v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
                v.isAvailable
            ).length > 0
          ) {
            console.debug(
              "copy: picture found match OK to copy! " + item?.selectedProduct?.settings.tgop.picture.selectedValue.id
            );
            items[i].selectedProduct!.settings.tgop.picture.selectedValue = items[
              i
            ].selectedProduct!.settings.tgop.picture.values.find(
              (v) =>
                v.format.format === item?.selectedProduct?.settings.tgop.picture.selectedValue.format.format &&
                v.isAvailable
            );
          } else
            console.debug(
              "copy: no picture found match to copy! " + item?.selectedProduct?.settings.tgop.picture.selectedValue.id
            );
        }

        // burned subtitles
        if (
          item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue === null ||
          item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue === undefined
        )
          items[i].selectedProduct!.settings.tgop.subtitleBurned.selectedValue = null;
        else if (
          items[i].selectedProduct!.settings.tgop.subtitleBurned.values.filter(
            (v) =>
              v.language?.value === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.language?.value &&
              v.type === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.type &&
              v.isSDH === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.isSDH &&
              v.isAvailable
          ).length > 0
        )
          items[i].selectedProduct!.settings.tgop.subtitleBurned.selectedValue = items[
            i
          ].selectedProduct!.settings.tgop.subtitleBurned.values.find(
            (v) =>
              v.language?.value === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.language?.value &&
              v.type === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.type &&
              v.isSDH === item?.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.isSDH &&
              v.isAvailable
          );

        // sidecar subtitles
        if (
          item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues !== null &&
          item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues.length > 0
        ) {
          for (
            let sub = 0;
            sub < item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues.length;
            ++sub
          ) {
            let matched = items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.values.find(
              (v) =>
                item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues[sub].language?.value ===
                  v.language?.value &&
                item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues[sub].type === v.type &&
                item?.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues[sub].isSDH === v.isSDH &&
                v.isAvailable
            );

            if (matched) {
              if (items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues === null)
                items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues = [];

              if (
                items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues.filter(
                  (v) =>
                    matched.language?.value === v.language?.value &&
                    matched.type === v.type &&
                    matched.lisSDH === v.isSDH &&
                    v.isAvailable
                ).length > 0
              )
                continue;

              items[i].selectedProduct!.settings.tgop.subtitleSidecarFiles.selectedValues.push(matched);
            }
          }
        }

        // subtitle file format
        if (
          item?.selectedProduct?.settings.tgop.fileFormatSubtitleSidecar.selectedValue !== null &&
          item?.selectedProduct?.settings.tgop.fileFormatSubtitleSidecar.selectedValue !== undefined
        )
          items[i].selectedProduct!.settings.tgop.fileFormatSubtitleSidecar.selectedValue = items[
            i
          ].selectedProduct!.settings.tgop.fileFormatSubtitleSidecar.values.find(
            (x) => x.id === item?.selectedProduct?.settings.tgop.fileFormatSubtitleSidecar.selectedValue.id
          );

        // timecode print
        items[i].selectedProduct!.settings.tgop.burnTimecode = item?.selectedProduct?.settings.tgop.burnTimecode;
        items[i].selectedProduct!.settings.tgop.videoFilters = item?.selectedProduct?.settings.tgop.videoFilters;

        // security overlay
        items[i].selectedProduct!.settings.tgop.overlayTextPlaceholder =
          item?.selectedProduct?.settings.tgop.overlayTextPlaceholder;
        items[i].selectedProduct!.watermarkTemplate = item?.selectedProduct?.watermarkTemplate;

        // watermarking
        items[i].selectedProduct!.watermarkSecurity = item?.selectedProduct?.watermarkSecurity;

        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;

        break;
      case OrderProductType.SubtitleConverter:
        // Technical Specifications
        if (
          item?.selectedProduct?.settings.fileFormat.selectedValue !== null &&
          item?.selectedProduct?.settings.fileFormat.selectedValue !== undefined
        )
          items[i].selectedProduct!.settings.fileFormat.selectedValue =
            item?.selectedProduct?.settings.fileFormat.selectedValue;
        if (
          item?.selectedProduct?.settings.frameRate.selectedValue !== null &&
          item?.selectedProduct?.settings.frameRate.selectedValue !== undefined
        )
          items[i].selectedProduct!.settings.frameRate.selectedValue =
            item?.selectedProduct?.settings.frameRate.selectedValue;

        // Generated Assets Management
        items[i].selectedProduct!.generatedAssetsVisible = item?.selectedProduct?.generatedAssetsVisible;

        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;
        break;

      case OrderProductType.TranscodeImf:
        items[i].selectedProduct!.settings.cpls.selectedValue = items[i].selectedProduct!.settings.cpls.values.find(
          (x) => x.summary === item?.selectedProduct?.settings.cpls.selectedValue.summary
        );

        if (items[i].selectedProduct!.settings.cpls.values.length === 1)
          items[i].selectedProduct!.settings.cpls.selectedValue = items[i].selectedProduct!.settings.cpls.values[0];

        if (!items[i].selectedProduct!.settings.cpls.selectedValue) break;

        let sourceValue = item?.selectedProduct?.settings.cpls.selectedValue;
        let destValue = items[i].selectedProduct!.settings.cpls.selectedValue;

        destValue.fileFormat.selectedValue = sourceValue.fileFormat.selectedValue;
        destValue.frameRate.selectedValue = sourceValue.frameRate.selectedValue;
        destValue.videoFieldOrder.selectedValue = sourceValue.videoFieldOrder.selectedValue;

        // Security overlay
        destValue.overlayTextPlaceholder = sourceValue.overlayTextPlaceholder;
        items[i].selectedProduct!.watermarkTemplate = item?.selectedProduct?.watermarkTemplate;

        items[i].durationInMinutes = sourceValue.duration;
        // Watermarking
        items[i].selectedProduct!.watermarkSecurity = item?.selectedProduct?.watermarkSecurity;
        // Generated Assets Management
        items[i].selectedProduct!.generatedAssetsVisible = item?.selectedProduct?.generatedAssetsVisible;
        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        // Delivery Mode
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;

        break;

      case OrderProductType.SubtitleScribeProject:
        items[i].selectedProduct!.settings = item?.selectedProduct?.settings;
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        items[i].manualTasksTemplateId = item?.manualTasksTemplateId;
        items[i].manualTasksTemplate = item?.manualTasksTemplate;

        break;

      case OrderProductType.TranscodeBundleByProfile:
        items[i].selectedProduct!.settings.targetFeatureVideoProfile =
          item?.selectedProduct?.settings.targetFeatureVideoProfile;
        items[i].selectedProduct!.settings.countries = item?.selectedProduct?.settings.countries;

        let allowedComponentTypes = item?.selectedProduct?.settings.components.map((y) => y.type);
        if (allowedComponentTypes.length > 0) {
          let updatedComponents = items[i].selectedProduct!.settings.components.filter((x) =>
            allowedComponentTypes.includes(x.type)
          );
          items[i].selectedProduct!.settings.components = updatedComponents;
        }
        break;

      case OrderProductType.DcpKdmGeneration:
        // KDM
        items[i].selectedProduct!.settings.selectedRecipientIds = item?.selectedProduct?.settings.selectedRecipientIds;
        items[i].selectedProduct!.settings.dateIn = item?.selectedProduct?.settings.dateIn;
        items[i].selectedProduct!.settings.dateOut = item?.selectedProduct?.settings.dateOut;
        // notification
        items[i].selectedProduct!.comment = item?.selectedProduct?.comment;
        items[i].selectedProduct!.emailNotificationList = item?.selectedProduct?.emailNotificationList;
        // Delivery Mode
        items[i].selectedProduct!.deliveryMethod = item?.selectedProduct?.deliveryMethod;
        items[i].selectedProduct!.deliveryStorageId = item?.selectedProduct?.deliveryStorageId;

        break;
      default:
        break;
    }
  }

  return items;
};
